'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

// ─── GUIDE WALKTHROUGH OVERLAY ────────────────────────────────────────────────

interface GuideStep {
  title: string;
  arabic: string;
  body: string;
  module?: string;
  moduleHref?: string;
  icon?: React.ReactNode;
}

interface ComponentGuide {
  number: string;
  title: string;
  subtitle: string;
  arabic: string;
  tag: string;
  accentColor: string;
  bgDark: string;
  primaryHref: string;
  primaryLabel: string;
  intro: string;
  steps: GuideStep[];
}

const COMPONENT_GUIDES: Record<string, ComponentGuide> = {
  keys: {
    number: '01',
    title: 'Keys to Kulli Haal',
    subtitle: 'Curriculum on the Fabric of Reality',
    arabic: 'كلي',
    tag: 'Curriculum',
    accentColor: '#2C4A3E',
    bgDark: 'linear-gradient(135deg, #1C2B25 0%, #2C4A3E 100%)',
    primaryHref: '/keys-to-kulli-haal',
    primaryLabel: 'Enter the Curriculum →',
    intro: 'Kulli Haal — every state of being. This curriculum is not a course to complete but a fabric to inhabit. It begins with the question: what is the nature of the reality you are moving through?',
    steps: [
      {
        title: 'The Five Keys',
        arabic: 'المفاتيح الخمسة',
        body: 'Five foundational keys unlock the curriculum: Amanah (Trust), Mizan (Balance), Khalifah (Stewardship), Tawakkul (Reliance), and Shukr (Gratitude). Each key is not a concept to memorize — it is a lens to apply.',
        module: 'Keys Overview',
        moduleHref: '/keys-to-kulli-haal',
      },
      {
        title: 'Ecological Sequences',
        arabic: 'التسلسلات البيئية',
        body: 'Seven ecological sequences — Mycorrhizal Networks, Metamorphosis, Salmon Run, Longleaf Pine, and more — each sequence is a living Ayat. The curriculum reads creation as a text.',
        module: 'Sequences S4–S7',
        moduleHref: '/keys-to-kulli-haal',
      },
      {
        title: 'God-Consciousness as Foundation',
        arabic: 'التقوى كأساس',
        body: 'The curriculum does not begin with action — it begins with recognition. Taqwa is not fear alone; it is the awareness that every state of being is a sign pointing back to its Author.',
        module: 'Taqwa Framework',
        moduleHref: '/keys-to-kulli-haal',
      },
      {
        title: 'Bridge to the IRL Lens',
        arabic: 'الجسر إلى العدسة',
        body: 'The Keys are the foundation. The IRL Lens is the application. After the curriculum, you carry the Keys into your locality — your neighborhood, your masjid, your daily walk.',
        module: 'Bridge: Keys → IRL',
        moduleHref: '/inner-reality-lens',
      },
    ],
  },
  ayat: {
    number: '02',
    title: 'Ayat in Creation',
    subtitle: 'Open-World Journey of Recognition',
    arabic: 'آيات',
    tag: 'Journey',
    accentColor: '#8B6914',
    bgDark: 'linear-gradient(135deg, #1A1200 0%, #3A2800 100%)',
    primaryHref: '/ayat-in-creation',
    primaryLabel: 'Begin the Journey →',
    intro: '"Soon We will show them Our Ayat in the horizons and in themselves." — Quran 41:53. This is not metaphor. It is a description of reality. Ayat in Creation is an open-world journey of learning to see.',
    steps: [
      {
        title: 'Signs in the Horizons',
        arabic: 'الآيات في الآفاق',
        body: 'Every tree, every watershed, every migration pattern — these are not decorations of a neutral universe. They are signs authored by Al-Khaliq, pointing back to the One who designed them.',
        module: 'Horizons Module',
        moduleHref: '/ayat-in-creation',
      },
      {
        title: 'Signs Within Yourselves',
        arabic: 'الآيات في أنفسهم',
        body: 'The Quran pairs the outer horizon with the inner self. Your own states — your grief, your wonder, your recognition — are also Ayat. The journey is both outward and inward simultaneously.',
        module: 'Inner Signs',
        moduleHref: '/ayat-in-creation',
      },
      {
        title: 'The Radial Signs Map',
        arabic: 'خريطة الآيات الشعاعية',
        body: 'An interactive map of signs across domains — ecological, social, personal, cosmic. Each node is a doorway. The map is not exhaustive; it is an invitation to extend it with your own observations.',
        module: 'Radial Map',
        moduleHref: '/ayat-in-creation',
      },
      {
        title: 'Continuous Practice',
        arabic: 'الممارسة المستمرة',
        body: 'Ayat in Creation is not a destination. There is no final screen. The practice is the point — a daily orientation of seeing the ordinary as extraordinary, the mundane as authored.',
        module: 'Living Practice',
        moduleHref: '/ayat-in-creation',
      },
    ],
  },
  irl: {
    number: '03',
    title: 'Inner Reality Lens',
    subtitle: 'Application for Recognition',
    arabic: 'باطن',
    tag: 'Application',
    accentColor: '#C4956A',
    bgDark: 'linear-gradient(135deg, #1A0E06 0%, #3A2010 100%)',
    primaryHref: '/inner-reality-lens',
    primaryLabel: 'Open the Lens →',
    intro: 'The IRL Lens is where the curriculum meets the street. It is a practical tool for recognizing things in creation as utilities and signs — turning the ordinary into a field of divine recognition.',
    steps: [
      {
        title: 'Eight Layers of Recognition',
        arabic: 'ثمانية طبقات من الاعتراف',
        body: 'From Mata (first encounter) through Examination, Recognition, and Re-Assembly to Uboor (crossing) — eight layers of deepening engagement with any object, person, or situation in your locality.',
        module: 'Recognition Arc',
        moduleHref: '/inner-reality-lens',
      },
      {
        title: 'Nine Cities, One Lens',
        arabic: 'تسع مدن، عدسة واحدة',
        body: 'The IRL Lens has been applied across Atlanta, Detroit, Chicago, Pittsburgh, Durham, Louisville, Portland, Cincinnati, and Omaha. Each city reveals different Ayat — the same lens, different terrain.',
        module: 'City Walkthroughs',
        moduleHref: '/inner-reality-lens',
      },
      {
        title: 'SLEDOA Framework',
        arabic: 'إطار سليدوا',
        body: 'Signs, Layers, Ecology, Design, Observation, Amanah — a structured method for moving from surface observation to deep recognition. Not a checklist but a rhythm of attention.',
        module: 'SLEDOA Method',
        moduleHref: '/inner-reality-lens',
      },
      {
        title: 'From Recognition to Action',
        arabic: 'من الاعتراف إلى العمل',
        body: 'Recognition without action is incomplete. The IRL Lens bridges to the Muslim Resource Guide — what you see in your locality becomes what you are called to serve. Observation is the beginning of Amanah.',
        module: 'Bridge: IRL → MRG',
        moduleHref: '/muslim-resource-guide',
      },
    ],
  },
  mrg: {
    number: '04',
    title: 'Muslim Resource Guide',
    subtitle: 'Community Caretaking Utility',
    arabic: 'أمة',
    tag: 'Community · Resource',
    accentColor: '#B38A2A',
    bgDark: 'linear-gradient(135deg, #0D1A10 0%, #1C2B25 100%)',
    primaryHref: '/muslim-resource-guide',
    primaryLabel: 'Open the Guide →',
    intro: 'The Muslim Resource Guide is a utility for servicing communities as caretakers. Not a directory — a living map of Amanah. Connecting people and efforts fisabilillah, linking individuals to address needs as Ayat in service.',
    steps: [
      {
        title: 'City-by-City Resources',
        arabic: 'موارد المدينة',
        body: 'Resources mapped across Atlanta, Detroit, Chicago, and beyond — housing, food, legal aid, mental health, education. Each resource is not just a listing; it is a node in the caretaker network.',
        module: 'City Resource Maps',
        moduleHref: '/muslim-resource-guide',
      },
      {
        title: 'Justice-Involved Resources',
        arabic: 'موارد العدالة',
        body: 'A dedicated section for brothers and sisters navigating the justice system — reentry support, legal resources, community connections. Rooted in Mizan: balance, not punishment, is the Islamic frame.',
        module: 'Justice Section',
        moduleHref: '/muslim-resource-guide',
      },
      {
        title: 'Caretaking Domains',
        arabic: 'مجالات الرعاية',
        body: 'Six caretaking domains — Ecological, Social, Educational, Economic, Spiritual, Physical — each domain is a field of Amanah. The MRG maps resources to domains, not just categories.',
        module: 'Caretaking Domains',
        moduleHref: '/muslim-resource-guide',
      },
      {
        title: 'Community Connect',
        arabic: 'التواصل المجتمعي',
        body: 'The MRG is not a static database. It is a living connection tool — linking caretakers to each other, to organizations, and to the needs in their locality. Fisabilillah is the operating principle.',
        module: 'Community Connect',
        moduleHref: '/muslim-resource-guide',
      },
    ],
  },
};

function GuideOverlay({ guideKey, onClose }: { guideKey: string; onClose: () => void }) {
  const guide = COMPONENT_GUIDES[guideKey];
  const [activeStep, setActiveStep] = useState(0);
  const [animating, setAnimating] = useState(false);

  if (!guide) return null;

  const goToStep = (idx: number) => {
    if (animating || idx === activeStep) return;
    setAnimating(true);
    setTimeout(() => {
      setActiveStep(idx);
      setAnimating(false);
    }, 180);
  };

  const step = guide.steps[activeStep];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl overflow-hidden"
        style={{
          background: guide.bgDark,
          border: `1px solid ${guide.accentColor}30`,
          maxHeight: '92vh',
          overflowY: 'auto',
          boxShadow: `0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px ${guide.accentColor}15`,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-8 pt-8 pb-6"
          style={{ borderBottom: `1px solid ${guide.accentColor}18` }}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <span
                className="font-mono text-[9px] tracking-widest uppercase block mb-2"
                style={{ color: `${guide.accentColor}80`, fontFamily: 'var(--font-mono)' }}
              >
                {guide.tag} · Component {guide.number}
              </span>
              <h2
                className="font-heading"
                style={{
                  fontSize: 'clamp(1.4rem, 3vw, 2rem)',
                  color: '#F5F0E8',
                  fontWeight: 600,
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                }}
              >
                {guide.title}
              </h2>
              <p className="font-body text-sm mt-1" style={{ color: `${guide.accentColor}90` }}>
                {guide.subtitle}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 ml-4">
              <span
                className="font-heading text-4xl opacity-20"
                style={{ color: guide.accentColor, fontStyle: 'italic' }}
              >
                {guide.arabic}
              </span>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full transition-all"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'rgba(245,240,232,0.4)',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                ✕
              </button>
            </div>
          </div>
          <p
            className="font-body text-sm leading-relaxed"
            style={{ color: 'rgba(245,240,232,0.6)', lineHeight: 1.75, fontStyle: 'italic' }}
          >
            {guide.intro}
          </p>
        </div>

        {/* Step navigation */}
        <div className="px-8 pt-6">
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            {guide.steps.map((s, i) => (
              <button
                key={i}
                onClick={() => goToStep(i)}
                className="font-mono text-[9px] tracking-widest uppercase px-3 py-1.5 rounded transition-all duration-200"
                style={{
                  background: activeStep === i ? `${guide.accentColor}20` : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${activeStep === i ? guide.accentColor + '50' : 'rgba(255,255,255,0.08)'}`,
                  color: activeStep === i ? guide.accentColor : 'rgba(245,240,232,0.35)',
                  cursor: 'pointer',
                }}
              >
                {String(i + 1).padStart(2, '0')} {s.title.split(' ').slice(0, 2).join(' ')}
              </button>
            ))}
          </div>

          {/* Active step content */}
          <div
            className="rounded-xl p-6 mb-6 transition-opacity duration-200"
            style={{
              background: `${guide.accentColor}08`,
              border: `1px solid ${guide.accentColor}20`,
              opacity: animating ? 0 : 1,
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <p
                  className="font-mono text-[9px] tracking-widest uppercase mb-1"
                  style={{ color: `${guide.accentColor}60` }}
                >
                  Step {activeStep + 1} of {guide.steps.length}
                </p>
                <h3
                  className="font-heading"
                  style={{
                    fontSize: 'clamp(1.1rem, 2vw, 1.4rem)',
                    color: '#F5F0E8',
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  {step.title}
                </h3>
              </div>
              <span
                style={{
                  fontFamily: "'Amiri', serif",
                  fontSize: 18,
                  color: `${guide.accentColor}50`,
                  direction: 'rtl',
                  flexShrink: 0,
                  marginLeft: 12,
                }}
              >
                {step.arabic}
              </span>
            </div>
            <p
              className="font-body text-sm leading-relaxed"
              style={{ color: 'rgba(245,240,232,0.7)', lineHeight: 1.8 }}
            >
              {step.body}
            </p>
            {step.module && step.moduleHref && (
              <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${guide.accentColor}15` }}>
                <Link
                  href={step.moduleHref}
                  className="font-mono text-[9px] tracking-widest uppercase flex items-center gap-2 transition-all duration-200 hover:gap-3 w-fit"
                  style={{ color: guide.accentColor }}
                >
                  Open {step.module}
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              </div>
            )}
          </div>

          {/* Step progress dots */}
          <div className="flex items-center gap-2 mb-6">
            {guide.steps.map((_, i) => (
              <button
                key={i}
                onClick={() => goToStep(i)}
                className="rounded-full transition-all duration-300"
                style={{
                  width: activeStep === i ? 20 : 6,
                  height: 6,
                  background: activeStep === i ? guide.accentColor : `${guide.accentColor}30`,
                  border: 'none',
                  cursor: 'pointer',
                }}
              />
            ))}
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <button
                onClick={() => goToStep(Math.max(0, activeStep - 1))}
                disabled={activeStep === 0}
                className="w-7 h-7 flex items-center justify-center rounded transition-all"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: activeStep === 0 ? 'rgba(255,255,255,0.15)' : 'rgba(245,240,232,0.5)',
                  cursor: activeStep === 0 ? 'not-allowed' : 'pointer',
                  fontSize: 12,
                }}
              >
                ←
              </button>
              <button
                onClick={() => goToStep(Math.min(guide.steps.length - 1, activeStep + 1))}
                disabled={activeStep === guide.steps.length - 1}
                className="w-7 h-7 flex items-center justify-center rounded transition-all"
                style={{
                  background: activeStep < guide.steps.length - 1 ? `${guide.accentColor}20` : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${activeStep < guide.steps.length - 1 ? guide.accentColor + '40' : 'rgba(255,255,255,0.08)'}`,
                  color: activeStep < guide.steps.length - 1 ? guide.accentColor : 'rgba(255,255,255,0.15)',
                  cursor: activeStep === guide.steps.length - 1 ? 'not-allowed' : 'pointer',
                  fontSize: 12,
                }}
              >
                →
              </button>
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div
          className="px-8 py-6 flex items-center justify-between gap-4"
          style={{ borderTop: `1px solid ${guide.accentColor}15` }}
        >
          <p
            className="font-mono text-[9px] tracking-widest uppercase"
            style={{ color: 'rgba(245,240,232,0.25)' }}
          >
            {guide.steps.length} modules · {guide.tag}
          </p>
          <Link
            href={guide.primaryHref}
            className="font-mono text-[10px] tracking-widest uppercase px-6 py-3 transition-all duration-300 flex items-center gap-2"
            style={{
              background: guide.accentColor,
              color: '#0a1a12',
              border: 'none',
              borderRadius: 4,
              fontWeight: 700,
            }}
          >
            {guide.primaryLabel}
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN SECTION ─────────────────────────────────────────────────────────────

export default function ComponentsBentoSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeGuide, setActiveGuide] = useState<string | null>(null);

  useEffect(() => {
    const elements = sectionRef.current?.querySelectorAll('.reveal-on-scroll');
    if (!elements) return;
    elements.forEach((el) => el.classList.add('hidden-initially'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const ExploreButton = ({ guideKey, color }: { guideKey: string; color: string }) => (
    <button
      onClick={() => setActiveGuide(guideKey)}
      className="font-mono text-xs tracking-wider uppercase flex items-center gap-2 transition-all duration-200 hover:gap-3"
      style={{ color, fontFamily: 'var(--font-mono)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
    >
      Explore
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );

  return (
    <>
      <section
        ref={sectionRef}
        id="components"
        className="relative overflow-hidden"
        style={{
          backgroundColor: '#EDE8DC',
          paddingTop: 96,
          paddingBottom: 96,
        }}
      >
        {/* Atmospheric background */}
        <div
          className="absolute bottom-0 left-0 w-80 h-80 pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(44,74,62,0.08) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />

        <div className="max-w-[1280px] mx-auto px-6 lg:px-8">

          {/* Section header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="reveal-on-scroll stagger-1 mb-4">
                <span className="arabic-badge">Four Components · المكونات الأربعة</span>
              </div>
              <h2
                className="reveal-on-scroll stagger-2 font-heading"
                style={{
                  fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
                  lineHeight: 1.1,
                  letterSpacing: '-0.025em',
                  color: '#1A1A14',
                  fontWeight: 600,
                  maxWidth: 560,
                }}
              >
                One Framework.{' '}
                <span style={{ color: '#8B6914', fontStyle: 'italic' }}>Four Expressions.</span>
              </h2>
            </div>
            <p
              className="reveal-on-scroll stagger-3 font-body leading-relaxed max-w-sm"
              style={{ fontSize: '1rem', color: '#6A6055' }}
            >
              Each component is interconnected — a living system rather than separate tools. Together they form a complete orientation for the Muslim caretaker.
            </p>
          </div>

          {/* Bento Grid — 2×2 asymmetric on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">

            {/* Card 01 — Keys to Kulli Haal — spans 3 cols, taller */}
            <div
              className="reveal-on-scroll stagger-1 component-card rounded-card p-7 flex flex-col justify-between lg:col-span-3 lg:row-span-2"
              style={{
                background: 'linear-gradient(135deg, #1C2B25 0%, #2C4A3E 100%)',
                border: '1px solid rgba(44,74,62,0.4)',
                minHeight: 340,
              }}
            >
              <div>
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="w-10 h-10 rounded flex items-center justify-center"
                    style={{ background: 'rgba(44,74,62,0.4)' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7EB89A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                  </div>
                  <span className="font-mono text-xs" style={{ color: '#7EB89A', fontFamily: 'var(--font-mono)', opacity: 0.5 }}>01</span>
                </div>
                <span className="font-mono text-xs tracking-widest uppercase block mb-3" style={{ color: '#7EB89A', fontFamily: 'var(--font-mono)' }}>Curriculum</span>
                <h3 className="font-heading mb-2" style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', lineHeight: 1.15, letterSpacing: '-0.02em', color: '#F5F0E8', fontWeight: 600 }}>
                  Keys to Kulli Haal
                </h3>
                <p className="font-body text-sm mb-1" style={{ color: 'rgba(245,240,232,0.5)' }}>Curriculum on the Fabric of Reality</p>
                <p className="font-body text-sm leading-relaxed mt-4" style={{ color: 'rgba(245,240,232,0.65)', lineHeight: 1.7 }}>
                  A structured curriculum exploring the nature of reality through an Islamic lens — the fabric of existence, God-Consciousness, and the signs embedded in every state of being. A foundation for understanding before acting.
                </p>
                <p className="font-body text-sm leading-relaxed mt-3" style={{ color: 'rgba(245,240,232,0.65)', lineHeight: 1.7 }}>
                  <em style={{ color: '#7EB89A' }}>Kulli (كلي)</em> — total, universal, comprehensive. This curriculum works from the totality of states, conditions, and realities as a map to God-Consciousness.
                </p>
              </div>
              <div className="flex flex-col gap-3 mt-6">
                <div className="flex flex-wrap gap-2">
                  {['Five Keys', 'Ecological Sequences', 'Taqwa Framework', 'God-Consciousness'].map(tag => (
                    <span
                      key={tag}
                      className="font-mono text-[8px] tracking-widest uppercase px-2 py-1 rounded"
                      style={{ background: 'rgba(44,74,62,0.5)', border: '1px solid rgba(126,184,154,0.25)', color: '#7EB89A' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-4xl opacity-10" style={{ color: '#7EB89A', fontStyle: 'italic' }}>كلي</span>
                  <ExploreButton guideKey="keys" color="#7EB89A" />
                </div>
              </div>
            </div>

            {/* Card 02 — Ayat in Creation */}
            <div
              className="reveal-on-scroll stagger-2 component-card rounded-card p-7 flex flex-col justify-between lg:col-span-3"
              style={{
                background: 'linear-gradient(135deg, #1A1200 0%, #3A2800 100%)',
                border: '1px solid rgba(179,138,42,0.25)',
                minHeight: 240,
              }}
            >
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: 'rgba(139,105,20,0.2)' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D4A82A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" /><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
                    </svg>
                  </div>
                  <span className="font-mono text-xs" style={{ color: '#D4A82A', fontFamily: 'var(--font-mono)', opacity: 0.5 }}>02</span>
                </div>
                <span className="font-mono text-xs tracking-widest uppercase block mb-2" style={{ color: '#D4A82A', fontFamily: 'var(--font-mono)' }}>Journey</span>
                <h3 className="font-heading mb-1" style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)', lineHeight: 1.2, letterSpacing: '-0.02em', color: '#F5F0E8', fontWeight: 600 }}>Ayat in Creation</h3>
                <p className="font-body text-sm leading-relaxed mt-3" style={{ color: 'rgba(245,240,232,0.65)', lineHeight: 1.65 }}>
                  An open-world journey of recognizing signs in creation that point back to Allah. Not a destination — a continuous practice of seeing.
                </p>
              </div>
              <div className="flex flex-col gap-3 mt-5">
                <div className="flex flex-wrap gap-2">
                  {['Signs in Horizons', 'Inner Signs', 'Radial Map', 'Living Practice'].map(tag => (
                    <span
                      key={tag}
                      className="font-mono text-[8px] tracking-widest uppercase px-2 py-1 rounded"
                      style={{ background: 'rgba(139,105,20,0.15)', border: '1px solid rgba(212,168,42,0.25)', color: '#D4A82A' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-3xl opacity-10" style={{ color: '#D4A82A', fontStyle: 'italic' }}>آيات</span>
                  <ExploreButton guideKey="ayat" color="#D4A82A" />
                </div>
              </div>
            </div>

            {/* Card 03 — Inner Reality Lens */}
            <div
              className="reveal-on-scroll stagger-3 component-card rounded-card p-7 flex flex-col justify-between lg:col-span-3"
              style={{
                background: 'linear-gradient(135deg, #1A0E06 0%, #3A2010 100%)',
                border: '1px solid rgba(196,149,106,0.25)',
                minHeight: 240,
              }}
            >
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: 'rgba(196,149,106,0.2)' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C4956A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /><path d="M11 8v6M8 11h6" />
                    </svg>
                  </div>
                  <span className="font-mono text-xs" style={{ color: '#C4956A', fontFamily: 'var(--font-mono)', opacity: 0.5 }}>03</span>
                </div>
                <span className="font-mono text-xs tracking-widest uppercase block mb-2" style={{ color: '#C4956A', fontFamily: 'var(--font-mono)' }}>Application</span>
                <h3 className="font-heading mb-1" style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)', lineHeight: 1.2, letterSpacing: '-0.02em', color: '#F5F0E8', fontWeight: 600 }}>Inner Reality Lens</h3>
                <p className="font-body text-sm leading-relaxed mt-3" style={{ color: 'rgba(245,240,232,0.65)', lineHeight: 1.65 }}>
                  A practical application for recognizing things in creation as utilities and signs — turning the ordinary into a field of divine recognition.
                </p>
              </div>
              <div className="flex flex-col gap-3 mt-5">
                <div className="flex flex-wrap gap-2">
                  {['8 Layers', 'Nine Cities', 'SLEDOA Method', 'Locality Practice'].map(tag => (
                    <span
                      key={tag}
                      className="font-mono text-[8px] tracking-widest uppercase px-2 py-1 rounded"
                      style={{ background: 'rgba(196,149,106,0.12)', border: '1px solid rgba(196,149,106,0.28)', color: '#C4956A' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-3xl opacity-10" style={{ color: '#C4956A', fontStyle: 'italic' }}>باطن</span>
                  <ExploreButton guideKey="irl" color="#C4956A" />
                </div>
              </div>
            </div>

            {/* Card 04 — Muslim Resource Guide — full width bottom */}
            <div
              className="reveal-on-scroll stagger-4 component-card rounded-card p-7 flex flex-col justify-between lg:col-span-6"
              style={{
                background: 'linear-gradient(135deg, #0D1A10 0%, #1C2B25 100%)',
                border: '1px solid rgba(179,138,42,0.2)',
                minHeight: 200,
              }}
            >
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: 'rgba(179,138,42,0.15)' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B38A2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 1 0 7.75" />
                      </svg>
                    </div>
                    <span className="font-mono text-xs" style={{ color: '#B38A2A', fontFamily: 'var(--font-mono)', opacity: 0.5 }}>04</span>
                  </div>
                  <span className="font-mono text-xs tracking-widest uppercase block mb-2" style={{ color: '#B38A2A', fontFamily: 'var(--font-mono)' }}>Community · Resource</span>
                  <h3 className="font-heading mb-1" style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)', lineHeight: 1.2, letterSpacing: '-0.02em', color: '#F5F0E8', fontWeight: 600 }}>
                    Muslim Resource Guide
                  </h3>
                  <p className="font-body text-sm mb-1" style={{ color: 'rgba(245,240,232,0.5)' }}>Community Caretaking Utility</p>
                  <p className="font-body text-sm leading-relaxed mt-3" style={{ color: 'rgba(245,240,232,0.65)', lineHeight: 1.65 }}>
                    A utility for servicing communities as caretakers. Connecting people and efforts fisabilillah — linking individuals to address needs, not as ends in themselves, but as Ayat in service of the community.
                  </p>
                </div>
                <div className="flex flex-col gap-3 md:items-end md:justify-between md:min-w-[200px]">
                  <div className="flex flex-wrap gap-2">
                    {['City Resources', 'Justice', 'Caretaking Domains', 'Community Connect'].map(tag => (
                      <span
                        key={tag}
                        className="font-mono text-[8px] tracking-widest uppercase px-2 py-1 rounded"
                        style={{ background: 'rgba(179,138,42,0.1)', border: '1px solid rgba(179,138,42,0.2)', color: '#B38A2A' }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between w-full md:justify-end md:gap-4 mt-4">
                    <span className="font-heading text-3xl opacity-10" style={{ color: '#B38A2A', fontStyle: 'italic' }}>أمة</span>
                    <ExploreButton guideKey="mrg" color="#B38A2A" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Connection note */}
          <div className="reveal-on-scroll stagger-4 mt-8 flex items-center gap-4">
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(44,74,62,0.2), transparent)' }} />
            <p className="font-mono text-xs tracking-wider text-center" style={{ color: '#8A8070', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
              Each component is an Ayat of the whole
            </p>
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(44,74,62,0.2), transparent)' }} />
          </div>
        </div>
      </section>

      {/* Guide Overlay */}
      {activeGuide && (
        <GuideOverlay guideKey={activeGuide} onClose={() => setActiveGuide(null)} />
      )}
    </>
  );
}